import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { type MultiValue, Select, type SingleValue } from 'chakra-react-select';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useCookie } from 'react-use';

import type { UserOverrides } from '@revelio/auth';
import { InterestedOptions, TourCustomPageValue } from '@revelio/core';
import type { Role, TrialType } from '@revelio/data-access';

import { videoMetadata } from '../../../../shared/ui/tutorial/Tutorial';
import {
  DEFAULT_TRIAL_EXPIRATION_DAYS,
  getTrialExpirationDate,
  type SelectOption,
  selectControlStyles,
} from './impersonationModal.utils';

type TourInterestOptionValue =
  | 'talentDiscovery'
  | 'dataBuilder'
  | 'talentDiscoveryAndDataBuilder';

const TOUR_INTEREST_OPTIONS: SelectOption<TourInterestOptionValue>[] = [
  { label: 'Talent Discovery', value: 'talentDiscovery' },
  { label: 'Data Builder', value: 'dataBuilder' },
  {
    label: 'Talent Discovery + Data Builder',
    value: 'talentDiscoveryAndDataBuilder',
  },
];

const parseTourInterestsFromCookie = (
  cookieValue?: string | null
): string[] => {
  if (!cookieValue) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(cookieValue) as unknown;
    if (Array.isArray(parsedValue)) {
      return parsedValue.filter(
        (value): value is string => typeof value === 'string'
      );
    }
  } catch {
    // Cookie can contain a single non-JSON interest value; fall through.
  }

  return cookieValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
};

const parseStepsCompleted = (value?: string): string[] => {
  if (!value) {
    return [];
  }

  try {
    const parsedValue = JSON.parse(value) as unknown;
    if (Array.isArray(parsedValue)) {
      return parsedValue.filter(
        (item): item is string => typeof item === 'string'
      );
    }
  } catch {
    return [];
  }

  return [];
};

interface ImpersonationUserOverridesFormProps {
  customOverrides: UserOverrides;
  setCustomOverrides: Dispatch<SetStateAction<UserOverrides>>;
  onReset: () => void;
  trialTypeOptions: SelectOption<TrialType>[];
  selectedTrialTypeOption: SelectOption<TrialType> | undefined;
  roleOptions: SelectOption<Role>[];
  selectedRoleOption: SelectOption<Role> | undefined;
}

export function ImpersonationUserOverridesForm({
  customOverrides,
  setCustomOverrides,
  onReset,
  trialTypeOptions,
  selectedTrialTypeOption,
  roleOptions,
  selectedRoleOption,
}: ImpersonationUserOverridesFormProps) {
  const [tourCustomPageValue] = useCookie(TourCustomPageValue);
  const cookieTourInterests = useMemo(
    () => parseTourInterestsFromCookie(tourCustomPageValue),
    [tourCustomPageValue]
  );
  const hasTalentAcquisitionTourInterest =
    customOverrides.tourInterestTalentAcquisition ??
    cookieTourInterests.includes(InterestedOptions.Talent_Acquisition);
  const hasCustomDataTourInterest =
    customOverrides.tourInterestCustomData ??
    cookieTourInterests.includes(
      InterestedOptions.Data_Enrichment_and_Custom_Reports
    );
  const selectedTourInterestOption = useMemo(() => {
    if (hasTalentAcquisitionTourInterest && hasCustomDataTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'talentDiscoveryAndDataBuilder'
      );
    }

    if (hasTalentAcquisitionTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'talentDiscovery'
      );
    }

    if (hasCustomDataTourInterest) {
      return TOUR_INTEREST_OPTIONS.find(
        (option) => option.value === 'dataBuilder'
      );
    }

    return undefined;
  }, [hasTalentAcquisitionTourInterest, hasCustomDataTourInterest]);
  const tutorialStepOptions = useMemo<SelectOption<string>[]>(
    () =>
      videoMetadata.flatMap(({ videos }) =>
        videos.map((video) => ({
          label: `${video.name} (${video.id})`,
          value: video.id,
        }))
      ),
    []
  );
  const selectedTutorialStepOptions = useMemo(() => {
    const seenSteps = new Set(
      parseStepsCompleted(customOverrides.stepsCompleted)
    );
    return tutorialStepOptions.filter((option) => seenSteps.has(option.value));
  }, [customOverrides.stepsCompleted, tutorialStepOptions]);

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="semibold">
          User Settings
        </Text>
        <Button
          size="xs"
          variant="link"
          colorScheme="blue"
          onClick={onReset}
          data-testid="reset-user-overrides-button"
        >
          Reset User Overrides
        </Button>
      </Flex>
      <VStack spacing={4} align="stretch">
        <FormControl id="trial-user-toggle">
          <FormLabel fontSize="sm" fontWeight="semibold">
            Trial User
          </FormLabel>
          <Flex alignItems="center">
            <FormLabel fontSize="sm">Off</FormLabel>
            <Switch
              id="trial-user-toggle"
              isChecked={customOverrides.isTrialUser}
              onChange={(event) =>
                setCustomOverrides((prev) => ({
                  ...prev,
                  isTrialUser: event.target.checked,
                  live: !event.target.checked,
                  expirationDate: event.target.checked
                    ? prev.expirationDate ||
                      getTrialExpirationDate(DEFAULT_TRIAL_EXPIRATION_DAYS)
                    : undefined,
                }))
              }
              colorScheme="green"
              data-testid="trial-user-toggle"
            />
            <FormLabel fontSize="sm" ml={3}>
              On
            </FormLabel>
          </Flex>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold">
            Trial Type
          </FormLabel>
          <Select<SelectOption<TrialType>, false>
            options={trialTypeOptions}
            value={selectedTrialTypeOption ?? null}
            placeholder="Select trial type..."
            onChange={(selected: SingleValue<SelectOption<TrialType>>) =>
              setCustomOverrides((prev) => ({
                ...prev,
                trialType: selected?.value,
              }))
            }
            isClearable={true}
            data-testid="trial-type-select"
            chakraStyles={selectControlStyles}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold">
            Trial End Date
          </FormLabel>
          <ReactDatePicker
            selected={
              customOverrides.expirationDate
                ? new Date(customOverrides.expirationDate)
                : null
            }
            onChange={(newDate: Date | null) =>
              setCustomOverrides((prev) => ({
                ...prev,
                expirationDate: newDate ? newDate.toISOString() : undefined,
              }))
            }
            dateFormat="MMMM d, yyyy h:mm aa"
            showTimeSelect={true}
            yearDropdownItemNumber={15}
            scrollableYearDropdown
            showYearDropdown={false}
            dropdownMode="select"
            customInput={<Input size="sm" />}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="semibold">
            Role Override
          </FormLabel>
          <Select<SelectOption<Role>, false>
            options={roleOptions}
            value={selectedRoleOption ?? null}
            placeholder="Select role..."
            onChange={(selected: SingleValue<SelectOption<Role>>) =>
              setCustomOverrides((prev) => ({
                ...prev,
                role: selected?.value,
              }))
            }
            isClearable={true}
            data-testid="role-override-select"
            chakraStyles={selectControlStyles}
          />
        </FormControl>

        <Accordion allowToggle>
          <AccordionItem
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="md"
          >
            <AccordionButton
              px={3}
              py={2}
              _hover={{ bg: 'gray.50' }}
              borderRadius="md"
              data-testid="user-tour-toggle"
            >
              <Text
                flex="1"
                textAlign="left"
                fontSize="sm"
                fontWeight="semibold"
              >
                User Tour
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={3} pb={3} pt={0}>
              <VStack spacing={4} align="stretch">
                <FormControl id="tour-completed-toggle">
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Tour Completed
                  </FormLabel>
                  <Flex alignItems="center">
                    <FormLabel fontSize="sm">Off</FormLabel>
                    <Switch
                      id="tour-completed-toggle"
                      isChecked={customOverrides.tourCompleted ?? false}
                      onChange={(event) =>
                        setCustomOverrides((prev) => ({
                          ...prev,
                          tourCompleted: event.target.checked,
                        }))
                      }
                      colorScheme="green"
                      data-testid="tour-completed-toggle"
                    />
                    <FormLabel fontSize="sm" ml={3}>
                      On
                    </FormLabel>
                  </Flex>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Interests Included in Tour
                  </FormLabel>
                  <Select<SelectOption<TourInterestOptionValue>, false>
                    options={TOUR_INTEREST_OPTIONS}
                    value={selectedTourInterestOption ?? null}
                    placeholder="Use current tour interests"
                    onChange={(
                      selected: SingleValue<
                        SelectOption<TourInterestOptionValue>
                      >
                    ) =>
                      setCustomOverrides((prev) => {
                        if (!selected) {
                          return {
                            ...prev,
                            tourInterestTalentAcquisition: undefined,
                            tourInterestCustomData: undefined,
                          };
                        }

                        if (selected.value === 'talentDiscovery') {
                          return {
                            ...prev,
                            tourInterestTalentAcquisition: true,
                            tourInterestCustomData: false,
                          };
                        }

                        if (selected.value === 'dataBuilder') {
                          return {
                            ...prev,
                            tourInterestTalentAcquisition: false,
                            tourInterestCustomData: true,
                          };
                        }

                        return {
                          ...prev,
                          tourInterestTalentAcquisition: true,
                          tourInterestCustomData: true,
                        };
                      })
                    }
                    isClearable={true}
                    data-testid="tour-interest-select"
                    chakraStyles={selectControlStyles}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="semibold">
                    Tutorial Steps Seen
                  </FormLabel>
                  <Select<SelectOption<string>, true>
                    isMulti
                    options={tutorialStepOptions}
                    value={selectedTutorialStepOptions}
                    placeholder="Choose seen tutorial steps..."
                    onChange={(selected: MultiValue<SelectOption<string>>) =>
                      setCustomOverrides((prev) => ({
                        ...prev,
                        stepsCompleted: JSON.stringify(
                          selected.map((option) => option.value)
                        ),
                      }))
                    }
                    isClearable={true}
                    closeMenuOnSelect={false}
                    data-testid="tutorial-steps-seen-select"
                    chakraStyles={selectControlStyles}
                  />
                  <FormHelperText>
                    This edits the metadata value `steps_completed` directly.
                  </FormHelperText>
                </FormControl>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </VStack>
    </Box>
  );
}
